import { supportedDocumentFormats } from "@finway-group/shared/lib/consts/common"
import {
    CostCenter,
    CostCenter2,
    DocumentTypeEnum,
    Employee,
    Expense,
    ExpenseKindEnum,
    ExpensePaymentFlowInformationEnum,
    ExpenseRequestTypeEnum,
    ExpenseStatusEnum,
    RightEnum,
    Vendor,
    VendorTypeEnum,
} from "@finway-group/shared/lib/models"
import { Button, Upload } from "antd"
import React from "react"
import { Upload as UploadIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import EmployeeLink from "Components/employeeLink"
import ExpenseStatusTag from "Components/expenseStatusTag"
import ExpenseTransactionStatusTag from "Components/expenseTransactionStatusTag"
import { useArchiveInterval } from "Shared/hooks/company.hooks"
import { useCardByExpense } from "Shared/hooks/creditCard.hooks"
import { ApprovalProcessService, AuthzService, ExpenseService } from "Shared/services"
import { getNextExpenseEmbeddedApprover } from "Shared/utils/approvalProcess.utils"
import { isExpenseArchived } from "Shared/utils/expense.utils"

import { formatExpenseDate } from "../../../../../shared/utils/date.utils"
import { UserCreditor } from "../../../../../shared/utils/vendor.utils"
import { CostCenter2Field } from "./costCenter2.field"
import { CostCenterField } from "./costCenter.field"
import { ExpenseAccountField } from "./expenseAccount.field"
import { InvoiceDateField } from "./invoiceDate.field"
import { InvoiceDatePaidAtField } from "./invoiceDatePaidAt.field"
import { InvoiceDueDateField } from "./invoiceDueDate.field"
import { InvoiceNumberField } from "./invoiceNumber.field"
import { PerformanceDateField } from "./performanceDate.field"
import { PostingPeriodField } from "./postingPeriod.field"
import { SubscriptionField } from "./subscription.field"
import { VendorField } from "./vendor.field"
import { WorkflowField } from "./workflow.field"

interface PurchaseTabProps {
    expense: Expense
    requestedBy: Employee
    costCenter?: CostCenter
    costCenter2?: CostCenter2
    vendorCreditor?: Vendor
    currentExpenseCreditor: UserCreditor | Vendor | undefined
    requestType: ExpenseRequestTypeEnum | undefined
    isDeliveryNoteUploading: boolean
    doFileUpload?: (file: any, kind: DocumentTypeEnum) => any
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    setSelectedExpense?: (expense: Expense | undefined) => void
    isExpenseUpdating?: boolean
    isOcrUploading?: boolean
    isReadOnly?: boolean
}

export const PurchaseTabContent = ({
    expense,
    requestedBy,
    costCenter,
    costCenter2,
    vendorCreditor,
    currentExpenseCreditor,
    requestType,
    doFileUpload,
    isReadOnly,
    isOcrUploading,
    isDeliveryNoteUploading,
    isExpenseUpdating,
    setIsExpenseUpdating,
    setSelectedExpense,
}: PurchaseTabProps) => {
    const { t } = useTranslation()
    const { status, kind } = expense
    const isSubscription = kind === ExpenseKindEnum.SUBSCRIPTION

    const archiveAfterXDays = useArchiveInterval()
    const archived = isExpenseArchived(expense, archiveAfterXDays)
    const isEditableBasedOnArchive = ExpenseService.isExpenseEditableBasedOnArchive(archived)

    const shouldShowInvoiceFields =
        (expense.status === ExpenseStatusEnum.INVOICE_PENDING || expense.status === ExpenseStatusEnum.APPROVED) && !expense.deleted && !isReadOnly && isEditableBasedOnArchive
    const isInvoiceFieldRequired = expense.status === ExpenseStatusEnum.APPROVED && expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY
    const { card } = useCardByExpense(expense)

    const isUserCreditor = expense?.vendorType === VendorTypeEnum.USER

    const areInputsDisabled = isOcrUploading || isExpenseUpdating
    const paymentTerm = !isUserCreditor ? (currentExpenseCreditor as Vendor)?.rule?.paymentTerm : undefined

    const nextApprover = ApprovalProcessService.getNextApprover(expense.approvalProcesses)
    const nextExpenseEmbeddedApprover = getNextExpenseEmbeddedApprover(expense.approvalProcesses)

    return (
        <ul className="description-list pb-10" id="expense-purchase-data-list">
            <li>
                <span className="title">{t("label:status")}</span>
                <span className="text">
                    <ExpenseStatusTag status={status} />
                </span>
            </li>
            <li>
                <span className="title">{t("label:purchased")}</span>
                <span data-testid="purchaseDateExpenseForm" className="text">
                    {formatExpenseDate(expense.datePurchased)}
                </span>
            </li>
            <PerformanceDateField expense={expense} />
            <li>
                <span className="title">{t("label:requested_by")}</span>
                <span className="text truncate max-w-250">
                    <EmployeeLink employee={requestedBy} expenseExtendedRefUser={expense.requestedBy} />
                </span>
            </li>
            {nextApprover && (
                <li>
                    <span className="title">{t("label:next_approver")}</span>
                    <span className="text truncate max-w-250">
                        <EmployeeLink employee={nextApprover} expenseExtendedRefUser={nextExpenseEmbeddedApprover} />
                    </span>
                </li>
            )}
            <WorkflowField expense={expense} />
            <CostCenterField
                expense={expense}
                setIsExpenseUpdating={setIsExpenseUpdating}
                setSelectedExpense={setSelectedExpense}
                costCenter={costCenter}
                requestedBy={requestedBy}
                isReadOnly={!!isReadOnly}
                areInputsDisabled={!!areInputsDisabled}
            />
            <CostCenter2Field expense={expense} costCenter2={costCenter2} />
            <ExpenseAccountField expense={expense} isInputDisabled={areInputsDisabled || isReadOnly} isLoading={isExpenseUpdating} />
            <VendorField
                expense={expense}
                setIsExpenseUpdating={setIsExpenseUpdating}
                setSelectedExpense={setSelectedExpense}
                currentExpenseCreditor={currentExpenseCreditor}
                vendorCreditor={vendorCreditor}
                isReadOnly={isReadOnly}
                areInputsDisabled={!!areInputsDisabled}
            />
            {card && (
                <li>
                    <span className="title">{t("label:attached_card")}</span>
                    <span className="text truncate max-w-300">
                        <Link to={`/credit-cards/${card?._id}`}>{`**** **** **** ${card?.last4Digits}`}</Link>
                    </span>
                </li>
            )}
            <SubscriptionField expense={expense} />
            {expense.paymentFlowInformation && (
                <li>
                    <span className="title">{t("label:payment_flow_information.title")}</span>
                    <span className="text">
                        <span>{t(`label:payment_flow_information.${expense.paymentFlowInformation}`)}</span>
                    </span>
                </li>
            )}
            {requestType !== ExpenseRequestTypeEnum.PURCHASE && (
                <>
                    <InvoiceNumberField
                        editable={shouldShowInvoiceFields}
                        expense={expense}
                        isLoading={areInputsDisabled}
                        required={isInvoiceFieldRequired}
                        setIsExpenseUpdating={setIsExpenseUpdating}
                    />
                    <InvoiceDateField
                        expense={expense}
                        setIsExpenseUpdating={setIsExpenseUpdating}
                        areInputsDisabled={!!areInputsDisabled}
                        shouldShowInvoiceFields={shouldShowInvoiceFields}
                        isInvoiceFieldRequired={isInvoiceFieldRequired}
                        paymentTerm={paymentTerm}
                    />
                    <InvoiceDueDateField
                        expense={expense}
                        areInputsDisabled={!!areInputsDisabled}
                        shouldShowInvoiceFields={shouldShowInvoiceFields}
                        isInvoiceFieldRequired={isInvoiceFieldRequired}
                        paymentTerm={paymentTerm}
                        setIsExpenseUpdating={setIsExpenseUpdating}
                    />
                    <InvoiceDatePaidAtField expense={expense} setIsExpenseUpdating={setIsExpenseUpdating} areInputsDisabled={!!areInputsDisabled} />
                    <PostingPeriodField isLoading={areInputsDisabled} expense={expense} editable={shouldShowInvoiceFields} />
                    {expense.isGobdCompliantInvoice() && (
                        <li>
                            <span className="title">{t("label:gobd.invoice_compliance_date")}</span>
                            <span className="text">{expense.gobdInvoiceComplianceDate ? formatExpenseDate(expense.gobdInvoiceComplianceDate) : "-"}</span>
                        </li>
                    )}
                    {expense.matchedTransactions?.length > 0 && AuthzService.isRightGrantedForLoggedInUser(RightEnum.TRANSACTION__ALL__READ) && (
                        <li>
                            <span className="title">{t("label:matching.transaction_status")}</span>
                            <span className="text">
                                <ExpenseTransactionStatusTag expense={expense} />
                            </span>
                        </li>
                    )}
                    {!isSubscription &&
                        doFileUpload !== undefined &&
                        !isReadOnly &&
                        expense.kind !== ExpenseKindEnum.MILEAGE &&
                        expense.kind !== ExpenseKindEnum.PER_DIEM &&
                        !expense.isGobdCompliantInvoice() &&
                        isEditableBasedOnArchive && (
                            <li>
                                <span className="grid-breakpoints hidden title xs:block xs:w-1/2 sm:hidden xl:block xl:w-1/2">{t("label:delivery_note")}</span>
                                <span className="grid-breakpoints w-full xs:w-1/2 sm:w-full xl:w-1/2 ant-btn p-0">
                                    <Upload
                                        customRequest={(req) => {
                                            doFileUpload([req.file], DocumentTypeEnum.DELIVERY_NOTE)
                                        }}
                                        accept={supportedDocumentFormats.join(",")}
                                        showUploadList={false}
                                        disabled={areInputsDisabled}
                                    >
                                        <Button loading={isDeliveryNoteUploading} disabled={expense.deleted} type="text" className="bg-transparent shadow-none">
                                            <UploadIcon />
                                            <span>{expense.deliveryNote ? t("action:replace_delivery_note") : t("action:upload_delivery_note")}</span>
                                        </Button>
                                    </Upload>
                                </span>
                            </li>
                        )}
                </>
            )}
        </ul>
    )
}
